import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Icons from '../components/Icons';
import WorkspaceKpis from '../components/workspace/WorkspaceKpis';
import SearchableChecklist from '../components/ui/SearchableChecklist';
import SearchableSelect from '../components/ui/SearchableSelect';
import StaffService from '../services/StaffService';
import MessageService from '../services/MessageService';
import ExternalMessageService from '../services/ExternalMessageService';
import JournalService from '../services/JournalService';
import PointsService from '../services/PointsService';
import { useUser } from '../store/UserContext';
import { useStudentStore } from '../store/StudentStore';
import { useSettings } from '../store/SettingsContext';
import { formatDateTime } from '../utils/dateFormat';

const sortByDate = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);

export default function InternalMessagesPage() {
  const { user } = useUser();
  const { settings } = useSettings();
  const { students } = useStudentStore();
  const location = useLocation();
  const staffService = useMemo(() => new StaffService(), []);
  const messageService = useMemo(() => new MessageService(), []);
  const externalMessageService = useMemo(() => new ExternalMessageService(), []);
  const journalService = useMemo(() => new JournalService(), []);
  const pointsService = useMemo(() => new PointsService(), []);
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('inbox');
  const [activeId, setActiveId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [references, setReferences] = useState([]);
  const [status, setStatus] = useState('');
  const [quickRecipientId, setQuickRecipientId] = useState('');
  const [showCompletedAssignments, setShowCompletedAssignments] = useState(false);
  const [quickReplyOpen, setQuickReplyOpen] = useState(false);
  const [quickReplyBody, setQuickReplyBody] = useState('');
  const [quickReplyStatus, setQuickReplyStatus] = useState('');
  const [, setJournalRevision] = useState(0);
  const [, setThreadTick] = useState(0);

  useEffect(() => {
    if (!user) return;
    setMessages(messageService.listForUser(user.id));
  }, [messageService, user]);

  useEffect(() => {
    const initial = location.state?.compose;
    if (!initial) return;
    setComposeOpen(true);
    setSubject(initial.subject || '');
    setBody(initial.body || '');
    setReferences(initial.references || []);
  }, [location.state]);

  const staffList = staffService.list();
  const studentMap = useMemo(
    () => new Map((students || []).map((student) => [student.id, student])),
    [students]
  );
  const staffRecipients = staffList
    .filter((staff) => staff.id !== user.id)
    .map((staff) => ({
      id: staff.id,
      name: staff.name,
      type: 'staff',
      meta: `${staff.role || staff.title || ''}${staff.loginEnabled ? '' : ' · Login disabled'}`
    }));
  const recipients = [...staffRecipients];
  const recipientMap = new Map(
    recipients.map((recipient) => [`${recipient.type}:${recipient.id}`, recipient])
  );
  const userId = user?.id || '';
  const inboxMessages = userId
    ? messages
        .filter(
          (message) =>
            message.toIds.includes(userId) ||
            (message.recipients || []).some((recipient) => recipient.type === 'staff' && recipient.id === userId)
        )
        .sort(sortByDate)
    : [];
  const sentMessages = userId
    ? messages.filter((message) => message.fromId === userId).sort(sortByDate)
    : [];

  const totalVisible = inboxMessages.length + sentMessages.length;
  const messageLimit = 50;
  const nearLimitThreshold = 45;

  const isAssignmentMessage = (message) => {
    const subject = message.subject?.toLowerCase() || '';
    const body = message.body?.toLowerCase() || '';
    return subject.includes('assignment') || subject.includes('assigned') || body.includes('assigned');
  };

  const getJournalId = (message) =>
    message?.references?.find((ref) => ref.type === 'journal')?.id || null;

  const isJournalMessage = (message) => Boolean(getJournalId(message));

  const isCriticalMessage = (message) => {
    const text = `${message.subject || ''} ${message.body || ''}`.toLowerCase();
    return ['critical', 'overdue', 'urgent', 'immediate', 'high risk', 'high-risk'].some((token) => text.includes(token));
  };

  const getAssignmentStatus = (message) => {
    const statusBy = message.assignment?.statusBy;
    if (statusBy && userId && statusBy[userId]) return statusBy[userId];
    if (message.assignment?.status) return message.assignment.status;
    return 'open';
  };

  const getJournalLogStatus = (message) => {
    if (!message || !userId) return 'open';
    const statusBy = message.logStatusBy || {};
    return statusBy[userId] || 'open';
  };

  const assignedMessages = inboxMessages.filter(
    (message) => message.assignment || isAssignmentMessage(message)
  );
  const journalMessages = inboxMessages.filter((message) => isJournalMessage(message));
  const criticalAssigned = assignedMessages.filter((message) => isCriticalMessage(message));
  const assignedOpen = assignedMessages.filter((message) => getAssignmentStatus(message) !== 'completed');
  const assignedCompleted = assignedMessages.filter((message) => getAssignmentStatus(message) === 'completed');
  const assignedVisible = showCompletedAssignments ? assignedMessages : assignedOpen;

  const journalEntries = journalService.list();
  const journalEntryById = useMemo(
    () => new Map(journalEntries.map((entry) => [entry.id, entry])),
    [journalEntries]
  );

  const journalReplyMap = (() => {
    const map = new Map();
    externalMessageService
      .list()
      .filter((message) => message.fromType === 'staff')
      .forEach((message) => {
        (message.references || [])
          .filter((ref) => ref.type === 'journal')
          .forEach((ref) => {
            const list = map.get(ref.id) || [];
            list.push(message);
            map.set(ref.id, list);
          });
      });
    return map;
  })();

  const repliedJournalMessages = journalMessages.filter((message) => {
    const journalId = getJournalId(message);
    return journalId && journalReplyMap.has(journalId);
  });
  const completedJournalMessages = journalMessages.filter(
    (message) => getJournalLogStatus(message) === 'completed'
  );
  const repliedOpenMessages = repliedJournalMessages.filter(
    (message) => getJournalLogStatus(message) !== 'completed'
  );

  const handledJournalIds = new Set(
    [...repliedOpenMessages, ...completedJournalMessages].map((message) => message.id)
  );
  const inboxVisibleMessages = inboxMessages.filter(
    (message) => !handledJournalIds.has(message.id)
  );

  const inboxUnreadCount = userId
    ? inboxVisibleMessages.filter((message) => !message.readBy.includes(userId)).length
    : 0;
  const starredCount = userId ? inboxVisibleMessages.filter((message) => message.starredBy?.includes(userId)).length : 0;
  const kpiItems = [
    { id: 'inbox', label: 'Received', value: inboxVisibleMessages.length },
    {
      id: 'assigned',
      label: 'Assigned jobs',
      value: assignedOpen.length,
      badge: criticalAssigned.length ? { label: 'Critical', variant: 'danger' } : undefined
    },
    { id: 'replied', label: 'Replied', value: repliedOpenMessages.length },
    { id: 'completed', label: 'Completed', value: completedJournalMessages.length },
    { id: 'unread', label: 'Unread', value: inboxUnreadCount, badge: { label: 'New', variant: 'danger' } },
    { id: 'starred', label: 'Starred', value: starredCount },
    { id: 'sent', label: 'Sent', value: sentMessages.length }
  ];

  const activeMessages =
    activeTab === 'sent'
      ? sentMessages
      : activeTab === 'assigned'
        ? assignedVisible
        : activeTab === 'replied'
          ? repliedOpenMessages
          : activeTab === 'completed'
            ? completedJournalMessages
            : inboxVisibleMessages;
  const scopedMessages = activeTab === 'unread'
    ? activeMessages.filter((message) => !message.readBy.includes(userId))
    : activeTab === 'starred'
      ? activeMessages.filter((message) => message.starredBy?.includes(userId))
      : activeMessages;
  const filteredMessages = scopedMessages.filter((message) => {
    const journalId = getJournalId(message);
    const journalEntry = journalId ? journalEntryById.get(journalId) : null;
    const studentRecord =
      (journalEntry && studentMap.get(journalEntry.studentId)) || studentMap.get(message.fromId);
    const studentMeta = [
      journalEntry?.studentName,
      studentRecord?.name,
      studentRecord?.reference,
      studentRecord?.id
    ]
      .filter(Boolean)
      .join(' ');
    const haystack = `${message.subject} ${message.body} ${message.fromName} ${message.toNames.join(' ')} ${studentMeta}`.toLowerCase();
    return haystack.includes(searchQuery.toLowerCase());
  });

  const activeMessage = filteredMessages.find((message) => message.id === activeId) ||
    scopedMessages.find((message) => message.id === activeId) ||
    activeMessages.find((message) => message.id === activeId);
  const isActiveAssignment = activeMessage && (activeMessage.assignment || isAssignmentMessage(activeMessage));
  const activeAssignmentStatus = activeMessage ? getAssignmentStatus(activeMessage) : null;
  const activeAssignmentCompletedAt = userId ? activeMessage?.assignment?.completedAtBy?.[userId] : null;
  const activeJournalLogStatus = activeMessage ? getJournalLogStatus(activeMessage) : null;
  const journalRef = activeMessage?.references?.find((ref) => ref.type === 'journal');
  const activeJournalEntry = journalRef ? journalEntries.find((entry) => entry.id === journalRef.id) : null;
  const journalThreadReplies = (() => {
    if (!journalRef) return [];
    return externalMessageService
      .list()
      .filter((message) => message.fromType === 'staff')
      .filter((message) => (message.references || []).some((ref) => ref.type === 'journal' && ref.id === journalRef.id))
      .sort(sortByDate);
  })();
  const senderStudent = useMemo(() => {
    if (!activeMessage) return null;
    const direct = studentMap.get(activeMessage.fromId);
    if (direct) return direct;
    if (activeJournalEntry) {
      return { id: activeJournalEntry.studentId, name: activeJournalEntry.studentName };
    }
    return null;
  }, [activeMessage, activeJournalEntry, studentMap]);
  const senderStaff = activeMessage
    ? staffList.find((staff) => staff.id === activeMessage.fromId)
    : null;

  if (!user) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Sign in to view internal messages.</p>
      </Card>
    );
  }

  const toggleRecipient = (id) => {
    setSelectedRecipients((prev) =>
      prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]
    );
  };

  const handleAddRecipient = (value) => {
    if (!value) return;
    const key = `staff:${value}`;
    if (!selectedRecipients.includes(key)) {
      setSelectedRecipients((prev) => [...prev, key]);
    }
    setQuickRecipientId('');
  };

  const handleSelectMessage = (message) => {
    setActiveId(message.id);
    if (activeTab !== 'sent' && !message.readBy.includes(user.id)) {
      messageService.markRead(message.id, user.id);
      setMessages(messageService.listForUser(user.id));
    }
  };

  const handleReply = () => {
    if (!activeMessage) return;
    setQuickReplyOpen(true);
    setQuickReplyBody('');
    setQuickReplyStatus('');
  };

  const handleReplyToMessage = (message) => {
    if (!message) return;
    setActiveId(message.id);
    setQuickReplyOpen(true);
    setQuickReplyBody('');
    setQuickReplyStatus('');
  };

  const handleForward = () => {
    if (!activeMessage) return;
    setComposeOpen(true);
    setSubject(activeMessage.subject.startsWith('Fwd:') ? activeMessage.subject : `Fwd: ${activeMessage.subject}`);
    setBody(`\n\n---\nForwarded message from ${activeMessage.fromName} on ${formatDateTime(activeMessage.createdAt)}:\n${activeMessage.body}`);
    setSelectedRecipients([]);
  };

  const toggleStar = (messageId) => {
    messageService.toggleStar(messageId, user.id);
    setMessages(messageService.listForUser(user.id));
  };

  const handleAssignmentStatus = (message, nextStatus) => {
    if (!message) return;
    messageService.updateAssignmentStatus(message.id, user.id, nextStatus);
    setMessages(messageService.listForUser(user.id));
  };

  const handleJournalLogStatus = (message, nextStatus) => {
    if (!message) return;
    messageService.updateLogStatus(message.id, user.id, nextStatus);
    setMessages(messageService.listForUser(user.id));
  };

  const handleDelete = () => {
    if (!activeMessage) return;
    const ok = window.confirm('Delete this message from your inbox?');
    if (!ok) return;
    messageService.deleteForUser(activeMessage.id, user.id);
    setMessages(messageService.listForUser(user.id));
    setActiveId(null);
  };

  const handleApproveJournal = () => {
    if (!activeJournalEntry || !activeMessage) return;
    if (activeJournalEntry.status === 'approved') return;
    journalService.updateEntry(activeJournalEntry.id, {
      status: 'approved',
      approvedBy: user?.name || 'Staff',
      approvedAt: new Date().toISOString(),
      pointsAwarded: activeJournalEntry.pointsAwarded || activeJournalEntry.category !== 'wellbeing'
    });
    if (activeJournalEntry.category === 'wellbeing' && !activeJournalEntry.pointsAwarded) {
      const student =
        studentMap.get(activeJournalEntry.studentId) || {
          id: activeJournalEntry.studentId,
          name: activeJournalEntry.studentName
        };
      const points = Number(settings.studentPortal?.pointsRules?.wellbeingCheckin || 0);
      if (student && settings.studentPortal?.pointsEnabled && points > 0) {
        pointsService.awardPoints({
          student,
          points,
          reason: 'Wellbeing check-in approved',
          source: 'wellbeing',
          createdBy: user?.name || 'Staff',
          thresholds: settings.studentPortal?.rewards || [],
          staffRecipients: []
        });
      }
      journalService.updateEntry(activeJournalEntry.id, { pointsAwarded: true });
      if (student) {
        externalMessageService.sendFromStaff({
          subject: 'Wellbeing check-in approved',
          body: `${student.name}, your wellbeing check-in has been reviewed and approved.`,
          fromUser: user || { id: 'system', name: 'SafeguardPro' },
          recipients: [{ id: student.id, name: student.name, type: 'student' }],
          category: 'wellbeing'
        });
      }
    }
    setJournalRevision((prev) => prev + 1);
  };

  const handleQuickReplySend = () => {
    if (!activeMessage || !quickReplyBody.trim()) {
      setQuickReplyStatus('Write a reply before sending.');
      return;
    }
    if (senderStudent) {
      const replyReferences = activeMessage.references?.length ? activeMessage.references : [];
      externalMessageService.sendFromStaff({
        subject: activeMessage.subject.startsWith('Re:') ? activeMessage.subject : `Re: ${activeMessage.subject}`,
        body: quickReplyBody.trim(),
        fromUser: user,
        recipients: [{ id: senderStudent.id, name: senderStudent.name, type: 'student' }],
        category: 'journal-reply',
        references: replyReferences
      });
      setQuickReplyStatus('Reply sent to student.');
      setQuickReplyBody('');
      setThreadTick((prev) => prev + 1);
      return;
    }
    if (senderStaff) {
      messageService.send({
        subject: activeMessage.subject.startsWith('Re:') ? activeMessage.subject : `Re: ${activeMessage.subject}`,
        body: quickReplyBody.trim(),
        fromUser: user,
        recipients: [{ id: senderStaff.id, name: senderStaff.name, type: 'staff' }],
        references: []
      });
      setMessages(messageService.listForUser(user.id));
      setQuickReplyStatus('Reply sent.');
      setQuickReplyBody('');
      setThreadTick((prev) => prev + 1);
    }
  };

  const handleSend = () => {
    setStatus('');
    if (!subject.trim()) {
      setStatus('Add a subject.');
      return;
    }
    if (selectedRecipients.length === 0) {
      setStatus('Select at least one recipient.');
      return;
    }
    const recipientRecords = selectedRecipients
      .map((key) => recipientMap.get(key))
      .filter(Boolean);
    messageService.send({
      subject: subject.trim(),
      body: body.trim(),
      fromUser: user,
      recipients: recipientRecords,
      references
    });
    setMessages(messageService.listForUser(user.id));
    setComposeOpen(false);
    setSubject('');
    setBody('');
    setSelectedRecipients([]);
    setReferences([]);
    setStatus('');
    setActiveTab('sent');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Internal Messages</h2>
          <p className="sp-muted">Share updates and forward incidents securely.</p>
        </div>
        <Button size="sm" onClick={() => setComposeOpen((prev) => !prev)}>
          {composeOpen ? 'Close compose' : 'New message'}
        </Button>
      </div>

      <WorkspaceKpis items={kpiItems} activeKpi={activeTab} onSelect={setActiveTab} />

      <div className="text-xs sp-muted">
        {totalVisible} messages shown. Messages are limited to the 50 most recent per user.
      </div>
      {totalVisible >= nearLimitThreshold && (
        <div className="sp-alert sp-alert--high text-sm">
          You are nearing the {messageLimit}-message limit. Older messages will be archived automatically.
        </div>
      )}

          {composeOpen && (
        <Card className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Recipients</label>
              <SearchableSelect
                value={quickRecipientId}
                options={staffRecipients.map((staff) => ({
                  value: staff.id,
                  label: staff.name,
                  meta: staff.meta
                }))}
                placeholder="Search staff"
                emptyLabel="No staff found."
                ariaLabel="Search staff"
                onChange={handleAddRecipient}
              />
              {selectedRecipients.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedRecipients.map((key) => {
                    const recipient = recipientMap.get(key);
                    if (!recipient) return null;
                    return (
                      <span key={key} className="sp-badge sp-badge--subtle">
                        {recipient.name}
                        <button
                          type="button"
                          className="sp-clear ml-2"
                          aria-label={`Remove ${recipient.name}`}
                          onClick={() => toggleRecipient(key)}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="mt-3">
                <SearchableChecklist
                  items={recipients}
                  selectedIds={selectedRecipients}
                  onToggle={toggleRecipient}
                  getId={(recipient) => `${recipient.type}:${recipient.id}`}
                  getLabel={(recipient) => recipient.name}
                  getMeta={(recipient) => `${recipient.meta ? recipient.meta : ''}`}
                  placeholder="Browse staff list"
                  emptyLabel="No staff available"
                />
              </div>
              <div className="text-xs sp-muted mt-2">
                Internal messages are staff-only. Use External Messages for student communications.
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="sp-label">Subject</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                />
              </div>
              <div>
                <label className="sp-label">Message</label>
                <textarea
                  className="sp-textarea mt-2 w-full"
                  rows={6}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                />
              </div>
              {references.length > 0 && (
                <div className="sp-panel sp-panel--muted p-3 text-xs">
                  <div className="sp-label">Forwarded items</div>
                  {references.map((ref) => (
                    <div key={`${ref.type}-${ref.id}`} className="mt-1">
                      {ref.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {status && <div className="text-xs sp-warning-text">{status}</div>}
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSend}>Send message</Button>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-[1.15fr_1fr] gap-4">
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="sp-value" style={{ fontWeight: 600 }}>Assigned jobs</div>
                <div className="text-xs sp-muted">Tasks and assignments routed to you.</div>
              </div>
              {criticalAssigned.length > 0 && (
                <Badge variant="danger">{criticalAssigned.length} critical</Badge>
              )}
            </div>
            {assignedMessages.length === 0 ? (
              <div className="sp-panel sp-panel--muted p-3 text-sm">No assignments yet.</div>
            ) : (
              <div className="space-y-2">
                {assignedMessages.slice(0, 5).map((message) => {
                  const critical = isCriticalMessage(message);
                  const status = getAssignmentStatus(message);
                  const isComplete = status === 'completed';
                  const completedAt = message.assignment?.completedAtBy?.[user.id];
                  return (
                    <button
                      key={message.id}
                      type="button"
                      className={`sp-panel w-full p-3 text-left ${
                        isComplete ? 'sp-panel--muted' : critical ? 'sp-panel--critical' : 'sp-panel--muted'
                      }`}
                      onClick={() => {
                        setActiveTab('inbox');
                        handleSelectMessage(message);
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="sp-value" style={{ fontWeight: 600 }}>
                          {message.subject}
                        </div>
                        <div className="text-xs sp-muted">{formatDateTime(message.createdAt)}</div>
                      </div>
                      <div className="text-xs sp-muted">From: {message.fromName}</div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <Badge variant={isComplete ? 'active' : 'warning'}>
                          {isComplete ? 'Completed' : 'Open'}
                        </Badge>
                        {completedAt && (
                          <span className="text-xs sp-muted">Completed {formatDateTime(completedAt)}</span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleReplyToMessage(message);
                          }}
                        >
                          Reply
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setActiveTab('inbox');
                            handleSelectMessage(message);
                          }}
                        >
                          Open
                        </Button>
                        <Button
                          size="sm"
                          variant={isComplete ? 'ghost' : 'secondary'}
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleAssignmentStatus(message, isComplete ? 'open' : 'completed');
                          }}
                        >
                          {isComplete ? 'Reopen' : 'Mark complete'}
                        </Button>
                      </div>
                      {critical && <div className="text-xs sp-warning-text mt-1">Critical attention</div>}
                    </button>
                  );
                })}
              </div>
            )}
            {assignedMessages.length > 5 && (
              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={() => {
                  setActiveTab('assigned');
                }}
              >
                View all assignments
              </Button>
            )}
          </Card>

          <Card className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="space-y-1">
                <div className="sp-label">
                  {activeTab === 'sent'
                    ? 'Sent messages'
                    : activeTab === 'unread'
                      ? 'Unread'
                      : activeTab === 'starred'
                        ? 'Starred'
                        : activeTab === 'assigned'
                          ? 'Assigned jobs'
                          : activeTab === 'replied'
                            ? 'Replied journals'
                            : activeTab === 'completed'
                              ? 'Completed logs'
                          : 'Received'}
                </div>
                {activeTab === 'assigned' && (
                  <div className="flex items-center gap-2 text-xs">
                    <Button
                      size="sm"
                      variant={showCompletedAssignments ? 'ghost' : 'secondary'}
                      type="button"
                      onClick={() => setShowCompletedAssignments(false)}
                    >
                      Open ({assignedOpen.length})
                    </Button>
                    <Button
                      size="sm"
                      variant={showCompletedAssignments ? 'secondary' : 'ghost'}
                      type="button"
                      onClick={() => setShowCompletedAssignments(true)}
                    >
                      Completed ({assignedCompleted.length})
                    </Button>
                  </div>
                )}
              </div>
              <input
                className="sp-input w-full md:w-64"
                placeholder={
                  activeTab === 'assigned'
                    ? 'Search assigned jobs'
                    : activeTab === 'replied' || activeTab === 'completed'
                      ? 'Search by student name or number'
                      : 'Search messages'
                }
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {filteredMessages.length === 0 && (
                <div className="sp-panel sp-panel--muted p-3 text-sm">No messages found.</div>
              )}
              {filteredMessages.map((message) => {
                const isUnread = !message.readBy.includes(user.id) && activeTab !== 'sent';
                const isStarred = message.starredBy?.includes(user.id);
                const isCritical = isCriticalMessage(message);
                const isAssignment = message.assignment || isAssignmentMessage(message);
                const assignmentStatus = isAssignment ? getAssignmentStatus(message) : null;
                const completedAt = message.assignment?.completedAtBy?.[user.id];
                const toPreview = message.toNames.length > 2
                  ? `${message.toNames.slice(0, 2).join(', ')} +${message.toNames.length - 2}`
                  : message.toNames.join(', ');
                return (
                  <button
                    key={message.id}
                    type="button"
                    className={`sp-panel w-full p-3 text-left ${
                      activeId === message.id
                        ? isCritical
                          ? 'sp-panel--critical'
                          : ''
                        : isCritical
                          ? 'sp-panel--warning'
                          : 'sp-panel--muted'
                    }`}
                    onClick={() => handleSelectMessage(message)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="sp-value flex items-center gap-2" style={{ fontWeight: 600 }}>
                        {isStarred && <Icons.Star filled />}
                        {message.subject}
                      </div>
                      <div className="text-xs sp-muted">{formatDateTime(message.createdAt)}</div>
                    </div>
                    <div className="text-xs sp-muted">
                      {activeTab === 'sent' ? `To: ${toPreview}` : `From: ${message.fromName}`}
                    </div>
                    {activeTab === 'assigned' && isAssignment && (
                      <div className="mt-1 flex items-center gap-2 text-xs">
                        <Badge variant={assignmentStatus === 'completed' ? 'active' : 'warning'}>
                          {assignmentStatus === 'completed' ? 'Completed' : 'Open'}
                        </Badge>
                        {completedAt && <span className="sp-muted">Completed {formatDateTime(completedAt)}</span>}
                      </div>
                    )}
                    {isUnread && <div className="text-xs sp-warning-text mt-1">Unread</div>}
                    {isCritical && <div className="text-xs sp-warning-text mt-1">Critical attention</div>}
                  </button>
                );
              })}
            </div>
          </Card>
        </div>

        <Card className="p-4 space-y-3">
          {!activeMessage ? (
            <div className="text-sm sp-muted">Select a message to view details.</div>
          ) : (
            <>
              <div>
                <div className="sp-value" style={{ fontWeight: 600 }}>{activeMessage.subject}</div>
                <div className="text-xs sp-muted">
                  {activeTab === 'sent'
                    ? `To: ${activeMessage.toNames.length} recipient${activeMessage.toNames.length === 1 ? '' : 's'}`
                    : `From: ${activeMessage.fromName}`} · {formatDateTime(activeMessage.createdAt)}
                </div>
                {activeTab === 'sent' && activeMessage.toNames.length > 0 && (
                  <div className="text-xs sp-muted mt-1">
                    {activeMessage.toNames.join(', ')}
                  </div>
                )}
              </div>
              {isActiveAssignment && (
                <div className="sp-panel sp-panel--muted p-3 text-xs flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="sp-label">Assigned job</div>
                    <div className="mt-1 flex items-center gap-2">
                      <Badge variant={activeAssignmentStatus === 'completed' ? 'active' : 'warning'}>
                        {activeAssignmentStatus === 'completed' ? 'Completed' : 'Open'}
                      </Badge>
                      {activeAssignmentCompletedAt && (
                        <span className="sp-muted">Completed {formatDateTime(activeAssignmentCompletedAt)}</span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={activeAssignmentStatus === 'completed' ? 'ghost' : 'secondary'}
                    type="button"
                    onClick={() =>
                      handleAssignmentStatus(
                        activeMessage,
                        activeAssignmentStatus === 'completed' ? 'open' : 'completed'
                      )
                    }
                  >
                    {activeAssignmentStatus === 'completed' ? 'Reopen task' : 'Mark complete'}
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                {activeTab !== 'sent' && (
                  <Button size="sm" variant="secondary" type="button" onClick={handleReply}>
                    Quick reply
                  </Button>
                )}
                <Button size="sm" variant="secondary" type="button" onClick={handleForward}>
                  Forward
                </Button>
                <Button size="sm" variant="ghost" type="button" onClick={() => toggleStar(activeMessage.id)}>
                  {activeMessage.starredBy?.includes(user.id) ? 'Unstar' : 'Star'}
                </Button>
                <Button size="sm" variant="ghost" type="button" onClick={handleDelete}>
                  Delete
                </Button>
                {activeJournalEntry && (
                  <Button
                    size="sm"
                    variant={activeJournalLogStatus === 'completed' ? 'ghost' : 'secondary'}
                    type="button"
                    onClick={() =>
                      handleJournalLogStatus(
                        activeMessage,
                        activeJournalLogStatus === 'completed' ? 'open' : 'completed'
                      )
                    }
                  >
                    {activeJournalLogStatus === 'completed' ? 'Reopen log' : 'Mark log complete'}
                  </Button>
                )}
                {activeJournalEntry?.category === 'wellbeing' && activeJournalEntry.status === 'pending' && (
                  <Button size="sm" variant="secondary" type="button" onClick={handleApproveJournal}>
                    Approve check-in & award points
                  </Button>
                )}
              </div>
              <div className="text-sm whitespace-pre-wrap">{activeMessage.body || 'No message body.'}</div>
              {activeJournalEntry && (
                <div className="sp-panel sp-panel--muted p-3 space-y-2 max-h-60 overflow-y-auto">
                  <div className="sp-label">Journal thread</div>
                  <div className="sp-panel sp-panel--muted p-2">
                    <div className="text-xs sp-muted">
                      {activeJournalEntry.studentName} · {formatDateTime(activeJournalEntry.createdAt)}
                    </div>
                    {activeJournalEntry.title && <div className="text-sm mt-1">{activeJournalEntry.title}</div>}
                    {activeJournalEntry.body && <div className="text-sm mt-1">{activeJournalEntry.body}</div>}
                  </div>
                  {journalThreadReplies.length === 0 ? (
                    <div className="text-xs sp-muted">No replies yet.</div>
                  ) : (
                    journalThreadReplies.map((reply) => (
                      <div key={reply.id} className="sp-panel sp-panel--muted p-2">
                        <div className="text-xs sp-muted">
                          {reply.fromName} · {formatDateTime(reply.createdAt)}
                        </div>
                        <div className="text-sm mt-1">{reply.body}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
              {activeMessage.references?.length > 0 && (
                <div className="sp-panel sp-panel--muted p-3 text-xs space-y-1">
                  <div className="sp-label">Forwarded items</div>
                  {activeMessage.references.map((ref) => (
                    <a key={`${ref.type}-${ref.id}`} href={ref.route} className="sp-link">
                      {ref.label}
                    </a>
                  ))}
                </div>
              )}
              {quickReplyOpen && (
                <div className="sp-panel sp-panel--muted p-3 space-y-2">
                  <div className="sp-label">
                    {senderStudent
                      ? `Reply to ${senderStudent.name} (student)`
                      : senderStaff
                        ? `Reply to ${senderStaff.name}`
                        : 'Reply'}
                  </div>
                  <textarea
                    className="sp-textarea w-full"
                    rows={3}
                    value={quickReplyBody}
                    onChange={(event) => setQuickReplyBody(event.target.value)}
                    placeholder="Write a quick reply"
                  />
                  {quickReplyStatus && <div className="text-xs sp-muted">{quickReplyStatus}</div>}
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="secondary" onClick={handleQuickReplySend}>
                      Send reply
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setQuickReplyOpen(false)}>
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
